import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { usePlan } from "../context/PlanContext";

/**
 * Slim trial banner shown across the app when the user is on the 7-day
 * Executive trial. Dismissible per-session; auto-hides once the trial ends
 * or the user is on a paid plan.
 */
export default function TrialBanner() {
  const { isTrial, trialSecondsLeft } = usePlan();
  const [dismissed, setDismissed] = useState(false);
  const [, setTick] = useState(0);

  // Re-render every 60s so the "Xd left" copy stays fresh
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!isTrial || dismissed) return null;

  const daysLeft = Math.max(0, Math.ceil(trialSecondsLeft / 86400));
  const hoursLeft = Math.max(0, Math.round(trialSecondsLeft / 3600));
  const label = daysLeft > 1 ? `${daysLeft} days` : `${hoursLeft}h`;
  const urgent = daysLeft <= 1;

  return (
    <div
      className="w-full border-b border-white/[0.05] bg-black/60 backdrop-blur-xl relative overflow-hidden"
      data-testid="trial-banner"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background: `linear-gradient(90deg, ${urgent ? "#ff4757" : "var(--bull)"}22, transparent 65%)`,
        }}
      />
      <div className="relative max-w-[1800px] mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: urgent ? "var(--bear)" : "var(--bull)" }} />
          <div className="text-[12px] sm:text-[13px] text-white/85 truncate">
            <span className="font-semibold text-white">Executive trial · {label} left.</span>
            <span className="hidden sm:inline text-white/60 ml-2">
              Enjoying it? Lock in your plan before it ends.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1 text-[12px] font-mono tracking-widest uppercase px-3 py-1 rounded-full border border-white/15 text-white hover:border-[var(--bull)]/60 hover:text-[var(--bull)] transition-colors"
            data-testid="trial-banner-cta"
          >
            Upgrade <ArrowRight className="w-3 h-3" />
          </Link>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss trial banner"
            className="text-white/40 hover:text-white p-1"
            data-testid="trial-banner-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
